import { useCallback, useEffect, useRef, useState } from 'react';
import { FormInstance, Modal, Upload } from 'antd';
import update from 'immutability-helper';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { BASE_API_URL, BASE_IMAGE_URL } from '../../env';
import DraggableUploadListItem from '../FileInput/DraggableUploadListItem';
import { getToken } from '../../service/CookieManager';
import ConfirmationModal from '../ConfirmationModal';
import { toastError, toastSuccess } from '../../service/Toast';

const token = getToken(`${BASE_API_URL}/utils/fileUpload`);

const { Dragger } = Upload;

interface IUploadImage {
  name: string;
  fieldValue?: any;
  fieldType: {
    accept?: string;
    maxCount?: number;
    allowImageCropping?: boolean;
    maxFileSize?: number;
    aspectRatio?: number;
  };
  shape?: 'rect' | 'round';
  form: FormInstance;
  nestedKey?: string;
  index?: any;
  placeholder?: string;
}

const getDefaultProps = (props: IUploadImage): any => ({
  name: 'image',
  multiple: false,
  action: `${BASE_API_URL}/news/upload`,
  headers: {
    Authorization: `Bearer ${token}`,
  },
  maxCount: props.fieldType.maxCount,
  listType: 'picture-card',
  accept: props.fieldType.accept,
});

const getPreviewUrl = (value?: string) => {
  if (
    !value ||
    value.startsWith('http') ||
    value.startsWith('blob:') ||
    value.startsWith('data:')
  ) {
    return value;
  }

  if (!BASE_IMAGE_URL) {
    return value;
  }

  return `${BASE_IMAGE_URL}${value.startsWith('/') ? value : `/${value}`}`;
};

const NewsArticleUploadImage = (props: IUploadImage) => {
  const { name, form, fieldValue, nestedKey, index, fieldType, placeholder } = props;
  const { maxCount, allowImageCropping, maxFileSize } = fieldType;

  const [fileList, setFileList] = useState<any>();
  const [imageCount, setImageCount] = useState<number>(0);

  const [openModal, setOpenModal] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>();
  const notifiedUploadUids = useRef<Set<string>>(new Set());

  const imageProps = getDefaultProps(props);

  useEffect(() => {
    if (fieldValue) {
      if (typeof fieldValue === 'string') {
        setImageCount(1);
      } else {
        setImageCount(fieldValue.length || 0);
      }

      const getFileList = (value: any) => {
        if (!value) return [];
        return (Array.isArray(value) ? value : [value]).map((url: string) => ({
          name: '',
          status: 'done',
          url: getPreviewUrl(url),
        }));
      };

      setFileList(getFileList(fieldValue));
    }
  }, [fieldValue]);

  const setUploadedValueInForm = (value: any) => {
    if (value.length === 0) {
      form.setFieldsValue({ [name]: null });
    }

    if (value.length === 1) {
      form.setFieldsValue({ [name]: value[0] });
    }

    if (value.length > 1) {
      form.setFieldsValue({ [name]: value });
    }
  };

  const beforeUpload = () => {
    if (imageCount == maxCount) {
      toastError(`Maximum limit to upload file is ${maxCount}`);
      return false;
    }
    return true;
  };

  const moveRow = useCallback(
    (dragIndex: any, hoverIndex: any) => {
      const dragRow = fileList[dragIndex];
      setFileList(
        update(fileList, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragRow],
          ],
        })
      );

      const rearrangedURLs = update(fileList, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRow],
        ],
      });

      form.setFieldsValue({
        [name]: rearrangedURLs.map((obj: any) => obj.url || obj.response.data),
      });
    },
    [fileList, name, form]
  );

  const onChange = (info: any) => {
    const { fileList, file } = info;
    const { status, size, uid } = file;

    switch (status) {
      case 'uploading': {
        const fileSizeCheck = (size: number, allowedSize?: number) => {
          if (!allowedSize) return true;
          return size / 1024 < allowedSize;
        };

        const isUploadAllowed = fileSizeCheck(size, maxFileSize);

        if (isUploadAllowed) {
          setImageCount(fileList.length);
          setFileList(fileList);
        } else {
          toastError(`${info.file.name} size is larger than the allowed size.`);
        }
        break;
      }

      case 'removed': {
        if (uid) {
          notifiedUploadUids.current.delete(uid);
        }
        setImageCount(fileList.length);
        setFileList(fileList);
        break;
      }

      case 'done': {
        setImageCount(fileList.length);
        const newFileList = fileList.map((f: any) => {
          if (f.response && f.response.data && !f.url) {
            f.url = getPreviewUrl(f.response.data);
            f.relativePath = f.response.data; // Store relative path for new uploads
          }
          return f;
        });
        setFileList(newFileList);
        // Ant Design Upload can fire "done" more than once for the same file
        if (uid && !notifiedUploadUids.current.has(uid)) {
          notifiedUploadUids.current.add(uid);
          toastSuccess(`${info.file.name} uploaded successfully.`);
        }
        const value = newFileList.map(
          (v: any) =>
            v.relativePath ||
            v.response?.data ||
            (v.url ? v.url.replace(BASE_IMAGE_URL || '', '') : '')
        );
        setUploadedValueInForm(value);
        break;
      }

      case 'error': {
        toastError(`${info.file.name} file upload failed.`);
      }
    }
  };

  const onRemove = (file: any) => {
    const filterFileList = fileList
      .filter((ele: any) => {
        const check = fieldValue ? ele.url : ele.response?.data;
        return check !== file.url;
      })
      .map((ele: any) => ele.url);
    setUploadedValueInForm(filterFileList);
  };

  const handleDeleteClick = (file: any) => {
    setDeletingItem(file);
    setOpenModal(true);
  };

  const handleConfirmDelete = async () => {
    setFileList([]);
    setImageCount(0);
    onRemove(deletingItem);
    setOpenModal(false);
    setDeletingItem(null);
  };

  const handleCancelDelete = () => {
    setOpenModal(false);
    setDeletingItem(null);
  };

  const onPreview = async (file: any) => {
    const { response, thumbUrl, url } = file;

    const imageUrl = getPreviewUrl(url || thumbUrl || response?.data);
    if (!imageUrl) {
      toastError('Preview is not available for this image.');
      return;
    }

    setPreviewImage(imageUrl);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewImage(undefined);
  };

  const previewModal = (
    <>
      <ConfirmationModal
        open={openModal}
        message={`Are you sure you want to delete?`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        onClose={handleCancelDelete}
      />
      <Modal
        visible={previewOpen}
        footer={null}
        centered
        width={800}
        onCancel={handleClosePreview}
        destroyOnClose
      >
        {previewImage && (
          <img
            alt="Preview"
            src={previewImage}
            style={{ display: 'block', maxHeight: '75vh', width: '100%', objectFit: 'contain' }}
          />
        )}
      </Modal>
    </>
  );

  return allowImageCropping ? (
    <>
      {previewModal}
      <DndProvider backend={HTML5Backend}>
        <Dragger
          fileList={fileList}
          {...imageProps}
          onChange={onChange}
          itemRender={(originNode, file, currFileList) => (
            <DraggableUploadListItem
              originNode={originNode}
              file={file}
              fileList={currFileList}
              moveRow={moveRow}
            />
          )}
          onRemove={file => {
            handleDeleteClick(file);
            return false;
          }}
          onPreview={onPreview}
          beforeUpload={beforeUpload}
          style={{ display: imageCount == maxCount && 'none' }}
        >
          <div
            style={{
              display: 'grid',
              placeItems: 'center',
              height: '102px',
              lineHeight: '17px',
              background: '#F8FAFB',
              color: '#637F92',
            }}
          >
            <span>{placeholder || 'Click to upload cover image'}</span>
            {maxFileSize && <span>{`Maximum file size allowed: ${maxFileSize} KB`}</span>}
          </div>
        </Dragger>
      </DndProvider>
    </>
  ) : (
    <>
      {previewModal}
      <Dragger
        fileList={fileList}
        {...imageProps}
        onChange={onChange}
        onRemove={onRemove}
        onPreview={onPreview}
        beforeUpload={beforeUpload}
        style={{ display: imageCount == maxCount && 'none' }}
      >
        <div
          style={{ height: '117px', lineHeight: '117px', background: '#F8FAFB', color: '#637F92' }}
        >
          Click to upload cover image
        </div>
      </Dragger>
    </>
  );
};

NewsArticleUploadImage.defaultProps = {
  allowImageCropping: true,
};

export default NewsArticleUploadImage;
